import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type BotoxIntensityWord = 'subtle' | 'moderate' | 'medium' | 'default' | 'strong' | 'high';
export type BotoxIntensity = 1 | 2 | 3;
export type BotoxTreatment = 1 | 2 | 3 | 4 | 5;

export interface GeminiBotoxImageInput {
  /**
   * Reference image data.
   * IMPORTANT: server expects base64 *without* the data URL prefix (recommended),
   * but it can also accept a full data URL string.
   */
  imageBase64: string;
  mimeType: string;

  /**
   * Either provide prompt directly OR (treatment + intensity).
   * If prompt is present, the server uses it and ignores treatment.
   */
  prompt?: string;

  /**
   * Treatment selector 1-5 (only used if prompt not provided)
   */
  treatment?: BotoxTreatment;

  /**
   * Intensity can be numeric 1-3 or a word the server maps:
   * subtle -> 1
   * moderate/medium/default -> 2
   * strong/high -> 3
   */
  intensity?: BotoxIntensity | BotoxIntensityWord;
}

export interface GeminiBotoxRequestBody {
  prompt?: string;
  treatment?: number; // 1-5
  intensity?: number | string; // 1-3 or mapped word
  image: {
    data: string;
    mimeType: string;
  };
}

export interface GeminiBotoxResponse {
  imageBase64?: string;
  model?: string;
  treatment?: number | null;
  intensity?: number;
  error?: string;
  message?: string;
  details?: any;
}

@Injectable({ providedIn: 'root' })
export class GeminiBotaxService {
  private readonly endpoint =
    'https://loevnj6no7crymysrsbdg6w3mi0qmoyo.lambda-url.us-east-1.on.aws/';

  constructor(private http: HttpClient) {}

  /**
   * Main entry: provide a reference image plus either:
   *  - prompt (custom)
   *  - OR treatment (1-5) + optional intensity (1-3 or word)
   *
   * Returns a data URL (data:<mime>;base64,...) string.
   */
  async generateBotoxPreview(input: GeminiBotoxImageInput): Promise<string> {
    const body = this.buildRequestBody(input);

    const response = await firstValueFrom(
      this.http.post<GeminiBotoxResponse>(this.endpoint, body, {
        headers: this.buildHeaders(),
      })
    );

    // Server may return 200 with { error } or 422 with { error, message } depending on integration
    if (response?.error) {
      const msg = response.message ? `${response.error}: ${response.message}` : response.error;
      throw new Error(msg);
    }

    if (!response?.imageBase64) {
      throw new Error('The AI provided a response but no image was found.');
    }

    return response.imageBase64;
  }

  /**
   * Backwards compatible helper for "edit" style calls (custom prompt).
   */
  async editImage(base64Image: string, mimeType: string, prompt: string): Promise<string> {
    return this.generateBotoxPreview({
      imageBase64: base64Image,
      mimeType,
      prompt,
    });
  }

  /**
   * Helper for treatment-based calls (1-5) with intensity as word or 1-3.
   */
  async applyTreatment(
    base64Image: string,
    mimeType: string,
    treatment: BotoxTreatment,
    intensity?: BotoxIntensity | BotoxIntensityWord
  ): Promise<string> {
    return this.generateBotoxPreview({
      imageBase64: base64Image,
      mimeType,
      treatment,
      intensity,
    });
  }

  // =========================
  // Request construction
  // =========================

  private buildRequestBody(input: GeminiBotoxImageInput): GeminiBotoxRequestBody {
    const imageData = this.stripDataUrlIfPresent(input.imageBase64);

    const body: GeminiBotoxRequestBody = {
      image: {
        data: imageData,
        mimeType: input.mimeType || 'image/png',
      },
    };

    // If prompt provided, use it as-is (server will treat treatment as optional/ignored)
    const prompt = (input.prompt || '').trim();
    if (prompt) {
      body.prompt = prompt;
      return body;
    }

    // Otherwise, require treatment
    if (!input.treatment || input.treatment < 1 || input.treatment > 5) {
      throw new Error('Missing prompt (or provide treatment 1-5).');
    }
    body.treatment = input.treatment;

    // Intensity default per server: moderate (2)
    if (input.intensity !== undefined && input.intensity !== null) {
      body.intensity = this.normalizeIntensity(input.intensity);
    } else {
      body.intensity = 2;
    }

    return body;
  }

  /**
   * Mirrors the server mapping:
   * subtle -> 1
   * moderate/medium/default -> 2
   * strong/high -> 3
   * Also accepts numeric 1-3; clamps to 1-3.
   */
  private normalizeIntensity(v: BotoxIntensity | BotoxIntensityWord): BotoxIntensity {
    // numeric
    if (typeof v === 'number') {
      return this.clampIntensity(v);
    }

    const s = String(v || '').trim().toLowerCase();

    // server mapping equivalence:
    // if s in ("subtle",): return 1
    if (s === 'subtle') return 1;

    // if s in ("moderate", "medium", "default"): return 2
    if (s === 'moderate' || s === 'medium' || s === 'default') return 2;

    // if s in ("strong", "high"): return 3
    if (s === 'strong' || s === 'high') return 3;

    // Also accept common variations
    if (s === 'med' || s === 'normal' || s === 'standard') return 2;

    // If unknown string, fall back to 2 (server's default behavior is 2 when missing)
    return 2;
  }

  private clampIntensity(n: number): BotoxIntensity {
    if (!Number.isFinite(n)) return 2;
    if (n <= 1) return 1;
    if (n === 2) return 2;
    return 3;
  }

  /**
   * Accepts either a data URL or raw base64 and returns raw base64.
   */
  private stripDataUrlIfPresent(s: string): string {
    const raw = String(s || '').trim();
    if (!raw) {
      throw new Error('No image provided.');
    }

    // If they passed data:<mime>;base64,<b64>
    const marker = ';base64,';
    if (raw.startsWith('data:') && raw.includes(marker)) {
      return raw.split(marker, 2)[1].trim();
    }

    return raw;
  }

  private buildHeaders(): HttpHeaders {
    // Lambda URL generally needs no special headers beyond JSON,
    // but keep this for future extension (auth, etc.)
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }
}